import { css } from '@emotion/react'
import { Icon, isEmpty, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { resolveName, SimNaoEnum, SimNaoRadioGroupField } from 'components/form'
import { RequiredLabel } from 'components/form/core/RequiredLabel/RequiredLabel'
import { useMemo } from 'react'
import { useField } from 'react-final-form'
import { MetaPath, MetaRoot } from 'util/metaPath'

import { ConsultaSeguimentoOutroFieldModel } from '../model-consultaSeguimento'
import { PerguntaSimNaoOutroField } from './PerguntaSimNaoOutroField'

export type ExemplosHorizontalColumnFieldModel<T extends string> = Record<T, SimNaoEnum | undefined>

export type ExemplosHorizontalColumnFieldComponents = {
  Append?: JSX.Element
}

interface ExemplosHorizontalColumnFieldProps<T extends string> {
  name: MetaPath<ExemplosHorizontalColumnFieldModel<T>>
  nameOutro?: MetaPath<ConsultaSeguimentoOutroFieldModel>
  label: string
  options: { value: T; label: string }[]
  required?: boolean
  components?: ExemplosHorizontalColumnFieldComponents
  showIndicator?: boolean
  showTagOptional?: boolean
  showOutroField?: boolean
  onChange?: () => void
}

export function ExemplosHorizontalColumnField<T extends string>(
  props: Readonly<ExemplosHorizontalColumnFieldProps<T>>
) {
  const {
    name,
    nameOutro,
    label,
    required,
    options,
    components,
    showIndicator = false,
    showTagOptional = false,
    showOutroField = false,
    onChange,
  } = props

  const {
    input: { value },
  } = useField<Record<T, SimNaoEnum>>(resolveName(name), {
    subscription: { value: true },
  })

  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme, showIndicator, showOutroField), [
    theme,
    showIndicator,
    showOutroField,
  ])

  const fields = name as MetaRoot<ExemplosHorizontalColumnFieldModel<T>>

  return (
    <VFlow vSpacing={0} style={styles.parent}>
      <div css={styles.root}>
        <div css={styles.header}>
          {required ? <RequiredLabel fontWeight='bold' label={label} /> : <Text fontWeight='bold'>{label}</Text>}
        </div>
        <VFlow vSpacing={0}>
          <VFlow vSpacing={0} style={styles.list}>
            {options.map((option) => {
              const mostrarOpcional = showTagOptional && isEmpty(value?.[option.value])

              return (
                <VFlow key={option.value} style={styles.listItem}>
                  <Text>
                    {option.label}
                    {mostrarOpcional && (
                      <span css={styles.tagOpcional}>
                        <span css={styles.tagOpcionalIconWrapper}>
                          <Icon size={1} icon='infoCircleOutline' />
                        </span>
                        <Text color='inherit'>Opcional</Text>
                      </span>
                    )}
                  </Text>
                  <SimNaoRadioGroupField name={fields[option.value]} hSpacing={0.5} onChange={onChange} />
                </VFlow>
              )
            })}
          </VFlow>
          {components?.Append && <div css={styles.appendWrapper}>{components?.Append}</div>}
        </VFlow>
      </div>
      {showOutroField && nameOutro && (
        <PerguntaSimNaoOutroField
          name={nameOutro}
          direction='vertical'
          showIndicator={showIndicator}
          style={[styles.listItem]}
          onChange={onChange}
        />
      )}
    </VFlow>
  )
}

const createStyles = (theme: Theme, showIndicator: boolean, showOutroField: boolean) => ({
  parent: css`
    &,
    & > div {
      height: 100%;
    }
  `,
  root: css`
    flex: 1;
    height: 100%;
    border-radius: 2px;
    border: 1px solid ${theme.pallete.divider};
    border-bottom: ${showOutroField ? '0' : `1px solid ${theme.pallete.divider}`};
    box-shadow: 0px 0px 0px ${showIndicator ? 2 : 0}px ${theme.pallete.primary.c60};
  `,
  header: css`
    padding: 0.5rem 1rem;
    background: ${theme.pallete.gray.c90};
  `,
  list: css`
    & > div:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
  listItem: css`
    padding: 0.75rem 1rem;
  `,
  appendWrapper: css`
    padding: 0 1rem;
  `,
  tagOpcional: css`
    color: ${theme.pallete.primary.main};
    margin-left: 0.75rem;
  `,
  tagOpcionalIconWrapper: css`
    margin-right: 0.25rem;
    svg {
      margin-bottom: -0.25rem;
    }
  `,
})
